<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { computed, watch } from "vue";
import { SatTabNavigation } from "@chatfood/design-system";
import type {
  ISatTabNavigationEmits,
  ISatTabNavigationProps,
} from "@chatfood/design-system";
import { MenuHandler } from "~/components/menu-handler";
import { useNavigation } from "~/composables/use-navigation";
import { useOutletStore } from "~/stores/outlet";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import { useAnalytics } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useRichCard } from "~/composables/use-rich-card";
import { useLocale } from "~/composables/use-locale";
import ItemModal from "~/modals/item/ItemModal.vue";

const props = defineProps<{
  orderingMode: IDomainOrderingMode;
  outletSlug: string;
  menuSlug: string;
}>();

const css = bemBuilder("pages-menu");

const { t } = useLocale();

const outlet = useOutletStore();
const business = useBusinessStore();

const { resolvePath, rawReplace, push } = useNavigation();

const menus = computed<ISatTabNavigationProps["items"]>(() => {
  return outlet.orderingMode.menus.map((menu) => ({
    id: menu.id,
    name: menu.name,
    link: resolvePath("menu", {
      menuSlug: menu.slug,
      orderingMode: props.orderingMode,
      outletSlug: props.outletSlug,
    }),
  }));
});

const activeMenu = computed(() => {
  return outlet.orderingMode.menus.find((menu) => menu.slug === props.menuSlug);
});

const title = t("pages.menu.seo_title", {
  menuName: activeMenu.value?.name ?? "",
  businessName: business.name,
});

const description = t("pages.menu.seo_description", {
  businessName: business.name,
});

const image = activeMenu.value?.icon || business.cover || business.logo;

useRichCard({ title, description, image });

function onBackPage() {
  push("outlet", {
    orderingMode: props.orderingMode,
    outletSlug: props.outletSlug,
  });
}

function onSelect(item: ISatTabNavigationEmits["select"][0]) {
  rawReplace(item.link);
}

const { trackEvent } = useAnalytics();
watch(
  activeMenu,
  (value) => {
    if (!value) return;

    trackEvent({
      name: "viewMenu",
      payload: {
        id: value.id,
        slug: value.slug,
      },
    });
  },
  { immediate: true },
);
</script>

<template>
  <div :class="css()">
    <SatTabNavigation
      v-if="outlet.orderingMode.menus.length > 1"
      :class="[
        css('nav'),
        { [css('nav', 'is-mobile-app')]: business.isMobileApp },
      ]"
      :items="menus"
      :active-item-id="activeMenu?.id"
      @back="onBackPage"
      @select="onSelect"
    />
    <MenuHandler
      :outlet-id="outlet.id"
      :menu-slug="props.menuSlug"
      :ordering-mode="props.orderingMode"
      :display-menu-cover="outlet.orderingMode.menus.length > 1"
    />
    <template v-if="activeMenu">
      <ItemModal :menu-id="activeMenu?.id" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.pages-menu {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);

  &__nav {
    padding-inline: var(--size-md);

    @media (width > $breakpoint-sm) {
      padding-inline: 0;
    }

    @media (width < $breakpoint-sm) {
      &--is-mobile-app {
        display: none;
      }
    }
  }

  @media (width > $breakpoint-sm) {
    gap: var(--spacing-lg);
  }

  &__order-mode {
    display: none;
    margin: var(--spacing-md);
  }

  @media (width < $breakpoint-lg) {
    &__order-mode {
      display: flex;
    }
  }

  @media (width <= $breakpoint-sm) {
    &__order-mode {
      width: 100%;
    }
  }
}
</style>
